import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AssistantLanguage,
  AssistantReadingResponse,
  AssistantSelectRequest,
  AssistantSuggestion,
} from '@chat-emp/shared';

import type { DraftTranslation } from '../conversation/AssistantCard.js';

import type { Message } from '../fixtures/domain.js';
import { apiFetch } from '../lib/api.js';
import { cidParts } from '../lib/bridge.js';
import { selectAssistantRow } from '../lib/facade.js';
import { useDraftTranslation } from './useDraftTranslation.js';

interface UseAssistant {
  /** null until the first answer decides it, which happens once per session. */
  enabled: boolean | null;
  /**
   * The lane is on and there is nothing to show for this conversation: the
   * request failed — an outage, no credit, no internet at all — or the reading
   * landed with no account, no mark and no drafts.
   *
   * One field for both because the footer asks one question of them: is there a
   * reading behind this card? An assistant that could not answer and one that
   * answered with nothing are the same card to an employer, and it is not the
   * assistant's.
   *
   * Still separate from `enabled`, which is a standing fact about the employer
   * rather than about one conversation.
   */
  silent: boolean;
  /** The backend has a re-read outstanding — never "a request is in flight". */
  reading: boolean;
  intro: string;
  suggest: AssistantSuggestion | null;
  replies: string[];
  /** The conversation's stored verdict; the card's header is the only place it is drawn. */
  flagged: boolean;
  /** The draft the assistant named, or null when it named none. */
  recommended: number | null;
  /** Absent while there is no stored reading with drafts to translate. */
  translation?: DraftTranslation;
  /**
   * The channel the held reading was drawn from, or null while none has landed.
   *
   * So a caller can tell a reading apart from the conversation now open. Anything
   * keyed on the open channel and this state together would otherwise pair one
   * channel's id with another channel's account for a render — which counted a
   * bogus card-shown row on every conversation switch.
   */
  readingFor: string | null;
  /**
   * Report what the employer took, against the audit row this reading came with.
   * A no-op without one — nothing to attribute a press to.
   */
  select: (kind: AssistantSelectRequest['takenKind'], value: string, edited?: boolean) => void;
}

/** What the hook holds internally. The audit id never reaches the card. */
interface Reading {
  auditId?: string;
  /** The channel this reading was drawn from. */
  channelId: string | null;
  intro: string;
  suggest: AssistantSuggestion | null;
  replies: string[];
  flagged: boolean;
  /** The conversation `flagged` was read for: it survives the two failure paths,
   *  which null `channelId`, and is never carried across a conversation. */
  flaggedFor: string | null;
  recommended: number | null;
  language: AssistantLanguage;
  reading: boolean;
  failed: boolean;
}

const SILENT: Reading = {
  channelId: null,
  intro: '',
  suggest: null,
  replies: [],
  flagged: false,
  flaggedFor: null,
  recommended: null,
  language: 'en',
  reading: false,
  failed: false,
};

const POLL_MS = 3_000;
// The scorer waits 60s after the last trigger and a read takes a few seconds
// more, so the card keeps asking for three minutes; the backend stops saying
// pending at five.
const POLL_CAP_MS = 180_000;
// The stamp lands a few seconds after the act (webhook, then a queue), after the
// read the act itself fires. So after an act in the open conversation the card
// keeps asking this long even while told "not pending".
const GRACE_MS = 15_000;
const NUDGE_EVENT = 'assistant:context-changed';

/** A note or summary save: nothing the card watches moves, so the save says so. */
export function nudgeAssistant(channelId: string): void {
  document.dispatchEvent(new CustomEvent(NUDGE_EVENT, { detail: channelId }));
}

/**
 * The stored verdict, carried across a reset — but only on the conversation it
 * was read for.
 *
 * `SILENT` is the reset value for the *reading*, not for the verdict. Spreading
 * it wholesale wiped `flagged` on every re-read and on both failure paths, which
 * is the opposite of what the field is documented to do: the header's dot
 * blinked off for the length of each re-read, and stayed off after a failed one
 * — on the one surface that still draws the verdict at all.
 *
 * The channel guard is the other half. A verdict belongs to one conversation, so
 * switching chats must land on no dot rather than inheriting the last one's —
 * and the same reset runs for both, since the effect re-fires on either.
 */
function heldVerdict(prev: Reading, channelId: string): Pick<Reading, 'flagged' | 'flaggedFor'> {
  return prev.flaggedFor === channelId
    ? { flagged: prev.flagged, flaggedFor: prev.flaggedFor }
    : { flagged: false, flaggedFor: null };
}

/**
 * Whether the assistant is on is a property of the session, not of a
 * conversation: the same employer gets the same answer every time. Remembering
 * the first one at module scope is what stops the second conversation asking
 * again — and, more visibly, stops the footer changing shape once per chat while
 * an answer is in flight.
 */
let sessionSwitch: boolean | null = null;

/** Test seam. Nothing in the app calls this: a page load is a fresh module. */
export function resetAssistantSwitch(): void {
  sessionSwitch = null;
}

/**
 * Whether the assistant is on, as far as this session has been told — null while
 * nothing has asked yet.
 *
 * For surfaces that must not ask. The widget shows the assistant by name only
 * and spends no reading, so it reads the answer the full page already got rather
 * than buying its own: asking costs a model call, since a switched-on lane draws
 * a reading to answer at all.
 *
 * The cost of that is a widget that stays quiet until the full page has run one
 * reading this session. Better than a panel that bills for a card it cannot draw.
 */
export function assistantSwitch(): boolean | null {
  return sessionSwitch;
}

/**
 * The assistant's reading of the open conversation.
 *
 * Deliberately a second hook rather than a mode of `useQuickReplies`: the two
 * lanes are switched independently and exactly one of them may fire, so keeping
 * them apart is what makes "turn the assistant off" mean something.
 *
 * Never throws and never surfaces an error. A failed reading is a silent
 * assistant, which the card already has a state for.
 */
export function useAssistant(
  channelId: string | null,
  messages: Message[],
  enabled: boolean,
): UseAssistant {
  const [state, setState] = useState<Reading>(SILENT);
  const [switchState, setSwitchState] = useState<boolean | null>(sessionSwitch);

  // Read the latest messages inside the effect via a ref, so the effect only
  // re-runs on the signature below — not on every new array identity.
  //
  // No ownership callback: unlike quick replies, the assistant does not care who
  // spoke last. It reads the conversation either way.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const signature = `${channelId ?? ''}:${messages.length}:${messages[messages.length - 1]?.id ?? ''}`;

  // A note or summary save in the open conversation: the message list does not
  // move, so the save says so itself (see `nudgeAssistant`).
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!channelId) return;
    const onNudge = (e: Event): void => {
      // The page holds the cid, the notes hook the bare id: compare the bare id.
      const nudged = cidParts((e as CustomEvent<string>).detail).channelId;
      if (nudged === cidParts(channelId).channelId) setRevision((r) => r + 1);
    };
    document.addEventListener(NUDGE_EVENT, onNudge);
    return () => document.removeEventListener(NUDGE_EVENT, onNudge);
  }, [channelId]);

  // Which conversation the previous run read for: an act in the same one is
  // what the grace window below is for, an open is not.
  const lastReadRef = useRef<string | null>(null);

  useEffect(() => {
    const msgs = messagesRef.current;
    if (!enabled || !channelId || msgs.length === 0) {
      setState(SILENT);
      return;
    }
    // Known off: the card is not rendered, so a reading would be spent on
    // nothing. The legacy lane is the one running in this case.
    if (sessionSwitch === false) return;

    const sameConversation = lastReadRef.current === channelId;
    lastReadRef.current = channelId;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let sawPending = false;
    const deadline = Date.now() + POLL_CAP_MS;
    const graceEnd = Date.now() + GRACE_MS;
    // A re-fetch for the open conversation keeps its reading on screen; only a
    // conversation switch lands on nothing.
    setState((prev) =>
      prev.channelId === channelId ? prev : { ...SILENT, ...heldVerdict(prev, channelId) },
    );

    const read = (): void => {
      apiFetch<AssistantReadingResponse>('/api/chat/assistant', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ channelId }),
      })
        .then((res) => {
          // The switch is remembered even from a superseded reading: which card to
          // draw is a session-wide fact, and it does not go stale when the
          // employer changes conversation mid-flight.
          sessionSwitch = res.enabled;
          if (cancelled) return;
          setSwitchState(res.enabled);
          // Lane on, reading unproduced: the footer falls back to the employer's
          // own controls (see `silent`).
          if (res.enabled && res.failed) {
            setState((prev) => ({ ...SILENT, ...heldVerdict(prev, channelId), failed: true }));
            return;
          }
          const now = Date.now();
          if (res.enabled && res.pending) sawPending = true;
          const pending = res.enabled && res.pending && now < deadline;
          // The act was just now and the stamp has not shown yet: keep asking
          // through the grace window. Once seen, `pending` alone decides.
          const expecting = res.enabled && sameConversation && !sawPending && now < graceEnd;
          setState(
            res.enabled
              ? {
                  ...(res.auditId ? { auditId: res.auditId } : {}),
                  channelId,
                  intro: res.intro,
                  suggest: res.suggest,
                  replies: res.replies,
                  flagged: res.flagged === true,
                  flaggedFor: channelId,
                  // Absent means no recommendation, never the first draft.
                  recommended: res.recommended ?? null,
                  language: res.language ?? 'en',
                  reading: pending,
                  failed: false,
                }
              : SILENT,
          );
          if (pending || expecting) timer = setTimeout(read, POLL_MS);
        })
        .catch(() => {
          if (cancelled) return;
          // A failure teaches nothing about the switch: keep what an earlier
          // reading established (null when none), never false.
          setSwitchState(sessionSwitch);
          setState((prev) => ({ ...SILENT, ...heldVerdict(prev, channelId), failed: true }));
        });
    };
    read();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [signature, revision, enabled, channelId]);

  // Held in a ref so `select` stays stable and always reports against the
  // reading actually on screen, rather than whichever render created it.
  const stateRef = useRef(state);
  stateRef.current = state;

  const drafts = useDraftTranslation(state);
  const languagesRef = useRef(drafts.languages);
  languagesRef.current = drafts.languages;

  const select = useCallback(
    (kind: AssistantSelectRequest['takenKind'], value: string, edited?: boolean) => {
      const { auditId } = stateRef.current;
      if (!auditId) return;
      void selectAssistantRow({
        auditId,
        takenKind: kind,
        takenValue: value,
        ...(edited === undefined ? {} : { edited }),
        ...(kind === 'reply' ? { language: languagesRef.current[Number(value)] } : {}),
      });
    },
    [],
  );

  // A reading that landed with nothing in it is as silent as one that never
  // landed: either way the card would wear the assistant's name with nothing
  // under it. Except while a re-read is outstanding: then the assistant has
  // something to say, that it is working, and a chat that has never been read
  // is exactly where the employer needs to see it. Silent again if the re-read
  // ends with nothing.
  //
  // Read against the OPEN channel, not merely against a landed reading. Only a
  // landed reading sets `channelId` at all, so the state on the way in — empty
  // for the same reason a blank page is — never counts; and on a conversation
  // switch the previous chat's verdict does not carry over for the render before
  // the next request starts, which would flash the wrong card into the slot.
  const silent =
    !state.reading &&
    (state.failed ||
      (state.channelId !== null &&
        state.channelId === channelId &&
        state.intro === '' &&
        state.suggest === null &&
        state.replies.length === 0));

  return {
    enabled: switchState,
    silent,
    reading: state.reading,
    intro: state.intro,
    suggest: state.suggest,
    replies: drafts.replies,
    flagged: state.flagged,
    recommended: state.recommended,
    ...(drafts.translation ? { translation: drafts.translation } : {}),
    readingFor: state.channelId,
    select,
  };
}
